import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { GroupedRoles, RoleType, UpdateRoleForEntityRequest } from '@models/authorization';
import {
  AllowedDomainsListResponse,
  CreateIndirectProviderRequest,
  IndirectProvider,
  IndirectProviderResponse,
  Partner,
  PartnerAdminUser,
  PartnerAdminUserResponse,
  PartnerBusinessData,
  PartnerGroup,
  PartnerListItem,
  SetPartnerMicrosoftInfoRequest,
  UpdatePartnerRequest,
} from '@models/partners';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable()
export class PartnersService {
  private readonly url = `${environment.bffUrl}/partners`;
  constructor(private httpClient: HttpClient) {}

  getAll(includeMarkedForDeletion = false): Observable<PartnerListItem[]> {
    return this.httpClient.get<PartnerListItem[]>(this.url, { params: { includeMarkedForDeletion } });
  }

  getOne(id: string): Observable<Partner> {
    return this.httpClient.get<Partner>(`${this.url}/${id}`);
  }

  update(id: string, partner: UpdatePartnerRequest): Observable<Partner> {
    return this.httpClient.put<Partner>(`${this.url}/${id}`, partner);
  }

  getPartnerBusinessData(id: string): Observable<PartnerBusinessData> {
    return this.httpClient.get<PartnerBusinessData>(`${this.url}/${id}/business-data`);
  }

  setPartnerRoles(id: string, roles: UpdateRoleForEntityRequest[], filter: RoleType, forceUpdate = false): Observable<GroupedRoles[]> {
    return this.httpClient.patch<GroupedRoles[]>(`${this.url}/${id}/roles`, roles, {
      params: forceUpdate ? { type: filter, forceUpdate } : { type: filter },
    });
  }

  validatePartnerRoles(id: string, roles: UpdateRoleForEntityRequest[]): Observable<boolean> {
    return this.httpClient.post<boolean>(`${this.url}/${id}/roles/any-in-use`, roles);
  }

  getPartnerRoles(id: string, filter: RoleType): Observable<GroupedRoles[]> {
    return this.httpClient.get<GroupedRoles[]>(`${this.url}/${id}/roles`, { params: { type: filter } });
  }

  markForDeletion(id: string): Observable<void> {
    return this.httpClient.post<void>(`${this.url}/${id}/mark-for-deletion`, undefined);
  }

  setCrmAccId(partnerId: string, crmAccountId: string): Observable<Partner> {
    return this.httpClient.post<Partner>(`${this.url}/${partnerId}/set-crm-account-id`, { crmAccountId });
  }

  getAdminUsers(partnerId: string): Observable<PartnerAdminUser[]> {
    return this.httpClient.get<PartnerAdminUserResponse[]>(`${this.url}/${partnerId}/administrators`);
  }

  setMicrosoftInfo(partnerId: string, req: SetPartnerMicrosoftInfoRequest | {}): Observable<Partner> {
    return this.httpClient.post<Partner>(`${this.url}/${partnerId}/set-expected-microsoft-info`, req);
  }

  getAllIndirectProviders(partnerId: string): Observable<IndirectProvider[]> {
    return this.httpClient.get<IndirectProviderResponse[]>(`${this.url}/${partnerId}/indirect-providers`);
  }

  getOneIndirectProvider(partnerId: string, indirectProviderId: string): Observable<IndirectProvider> {
    return this.httpClient.get<IndirectProviderResponse>(`${this.url}/${partnerId}/indirect-providers/${indirectProviderId}`);
  }

  createIndirectProvider(partnerId: string, req: CreateIndirectProviderRequest): Observable<IndirectProvider> {
    return this.httpClient.post<IndirectProviderResponse>(`${this.url}/${partnerId}/indirect-providers`, req);
  }

  deleteIndirectProvider(partnerId: string, indirectProviderId: string): Observable<void> {
    return this.httpClient.delete<void>(`${this.url}/${partnerId}/indirect-providers/${indirectProviderId}`);
  }

  activatePartner(partnerId: string): Observable<Partner> {
    return this.httpClient.post<Partner>(`${this.url}/${partnerId}/activate`, undefined);
  }

  deactivatePartner(partnerId: string): Observable<Partner> {
    return this.httpClient.post<Partner>(`${this.url}/${partnerId}/deactivate`, undefined);
  }

  getAllowedDomains(partnerId: string): Observable<AllowedDomainsListResponse> {
    return this.httpClient.get<AllowedDomainsListResponse>(`${this.url}/${partnerId}/allowed-domains`);
  }

  getPartnerCustomerGroups(partnerId: string): Observable<PartnerGroup[]> {
    return this.httpClient.get<PartnerGroup[]>(`${this.url}/${partnerId}/customer-groups`);
  }

  getPartnerEnvironmentGroups(partnerId: string): Observable<PartnerGroup[]> {
    return this.httpClient.get<PartnerGroup[]>(`${this.url}/${partnerId}/environment-groups`);
  }
}
